import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API, fmtInt, fmtPct, fmtMoney } from "@/api";
import { useI18n } from "@/i18n";
import { useShowPrices } from "@/showPrices";
import { PageHeader, KPI, Tile, Loading, StatusPill } from "@/components/Bits";
import {
  CaretLeft, Users, Target, ClipboardText, CalendarCheck, FilePdf, Sparkle,
  Warning, Trophy, ClockCounterClockwise, ArrowRight, EnvelopeSimple, Phone,
  ChatCircleDots, PlusCircle, PencilSimple, Trash, CheckCircle, XCircle, Train,
} from "@phosphor-icons/react";

const TIMELINE_META = {
  visit:          { Icon: CalendarCheck, color: "#0D71DD" },
  comment:        { Icon: ChatCircleDots, color: "#1d1d1f" },
  create:         { Icon: PlusCircle, color: "#34C759" },
  update:         { Icon: PencilSimple, color: "#FF9500" },
  delete:         { Icon: Trash, color: "#ED2027" },
  outcome_won:    { Icon: CheckCircle, color: "#34C759" },
  outcome_lost:   { Icon: XCircle, color: "#ED2027" },
  outcome_expired:{ Icon: XCircle, color: "#86868B" },
  contact_added:  { Icon: Users, color: "#0D71DD" },
};

export default function OperatorProfile() {
  const { name } = useParams();
  const navigate = useNavigate();
  const { lang } = useI18n();
  const { showPrices } = useShowPrices();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const L = (fr, en) => (lang === "fr" ? fr : en);

  useEffect(() => {
    setP(null); setErr(null); setAnalysis(null);
    api.get(`/operators/${encodeURIComponent(name)}/profile`)
      .then((r) => setP(r.data))
      .catch((e) => setErr(e.response?.status === 404 ? "notfound" : "error"));
  }, [name]);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const r = await api.post(`/operators/${encodeURIComponent(name)}/analyze`, { lang });
      setAnalysis(r.data.analysis);
    } catch {
      setAnalysis(L("⚠️ L'analyse a échoué — réessayez.", "⚠️ Analysis failed — please retry."));
    } finally {
      setAnalyzing(false);
    }
  };

  if (err) {
    return (
      <div className="p-10 text-center" data-testid="operator-not-found">
        <p className="text-[15px] mb-3">{L("Opérateur introuvable.", "Operator not found.")}</p>
        <button onClick={() => navigate("/")} className="btn-ghost">{L("Retour au tableau de bord", "Back to dashboard")}</button>
      </div>
    );
  }
  if (!p) return <Loading />;

  const k = p.kpis;
  const stale = p.days_since_interaction === null || p.days_since_interaction > 90;

  return (
    <div data-testid="operator-profile-page">
      <PageHeader
        title={
          <span className="flex items-center gap-3 flex-wrap">
            <button onClick={() => navigate(-1)} className="p-1.5 rounded-[8px] hover:bg-[var(--bg-subtle)] transition-colors -ml-2"
                    data-testid="operator-back" aria-label="Back">
              <CaretLeft size={20} weight="bold" />
            </button>
            {p.operator}
            {stale && (
              <span className="pill flex items-center gap-1" style={{ background: "rgba(255,149,0,0.12)", color: "#B36B00", border: "1px solid rgba(255,149,0,0.4)" }}
                    data-testid="operator-stale-badge">
                <Warning size={11} weight="fill" />
                {p.days_since_interaction === null
                  ? L("Aucune interaction", "No interaction yet")
                  : L(`Dernière interaction il y a ${p.days_since_interaction} j`, `Last interaction ${p.days_since_interaction}d ago`)}
              </span>
            )}
            {!stale && p.days_since_interaction !== null && (
              <span className="pill" style={{ background: "rgba(52,199,89,0.10)", color: "#1E7B36", border: "1px solid rgba(52,199,89,0.35)" }}
                    data-testid="operator-fresh-badge">
                {L(`Actif — il y a ${p.days_since_interaction} j`, `Active — ${p.days_since_interaction}d ago`)}
              </span>
            )}
          </span>
        }
        subtitle={`${k.cities.join(" · ")} — ${k.platforms_count} ${L("plateformes", "platforms")}, ${fmtInt(k.trainsets)} ${L("rames", "trainsets")}`}
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={analyze} disabled={analyzing} className="btn-ghost flex items-center gap-1.5"
                    data-testid="operator-analyze-btn">
              <Sparkle size={14} weight="fill" style={{ color: "var(--accent)" }} />
              {analyzing ? L("Analyse…", "Analyzing…") : L("Analyser ce compte", "Analyze account")}
            </button>
            <a href={`${API}/operators/${encodeURIComponent(name)}/profile.pdf?lang=${lang}`}
               target="_blank" rel="noopener noreferrer" className="btn-accent flex items-center gap-1.5"
               data-testid="operator-pdf-btn">
              <FilePdf size={14} weight="bold" /> PDF
            </a>
          </div>
        }
      />

      <div className="p-4 md:p-10 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 stagger">
          <KPI testId="op-kpi-units" label={L("Base installée", "Installed base")} value={fmtInt(k.units)} sub={L("unités", "units")} />
          <KPI testId="op-kpi-wabtec" label="Wabtec" value={fmtInt(k.wabtec_units)} accent sub={`${fmtPct(k.share_pct)} ${L("du parc", "of fleet")}`} />
          <KPI testId="op-kpi-opps" label={L("Opportunités", "Opportunities")} value={fmtInt(k.opportunities)} />
          {showPrices && p.pipeline_eur !== undefined ? (
            <KPI testId="op-kpi-pipeline" label="Pipeline M3" value={fmtMoney(p.pipeline_eur)} accent />
          ) : (
            <KPI testId="op-kpi-visits" label={L("Visites", "Visits")} value={fmtInt(k.visits)} />
          )}
        </div>

        {/* Relationship gauge + Win/Loss + Account plan */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 stagger">
          <Tile testId="op-relationship">
            <div className="label-tech mb-3">{L("Relation client", "Relationship")}</div>
            {p.avg_relationship ? (
              <>
                <div className="flex items-end gap-2">
                  <span className="num text-[30px] font-semibold leading-none"
                        style={{ color: p.avg_relationship >= 3.5 ? "#1E7B36" : p.avg_relationship >= 2.5 ? "#B36B00" : "var(--accent)" }}>
                    {p.avg_relationship}
                  </span>
                  <span className="text-[13px] mb-0.5" style={{ color: "var(--text-tertiary)" }}>/ 5</span>
                </div>
                <div className="mt-3 h-2 rounded-full overflow-hidden" style={{ background: "var(--bg-subtle)" }}>
                  <div className="h-full rounded-full transition-all"
                       style={{ width: `${(p.avg_relationship / 5) * 100}%`,
                                background: p.avg_relationship >= 3.5 ? "#34C759" : p.avg_relationship >= 2.5 ? "#FF9500" : "#ED2027" }} />
                </div>
                <div className="mt-2 text-[11.5px]" style={{ color: "var(--text-tertiary)" }}>
                  {L(`Score moyen sur ${k.contacts} contact(s)`, `Average over ${k.contacts} contact(s)`)}
                </div>
              </>
            ) : (
              <div className="text-[13px] py-3" style={{ color: "var(--text-tertiary)" }}>
                {L("Aucun contact noté pour cet opérateur.", "No scored contacts for this operator.")}
              </div>
            )}
          </Tile>

          <Tile testId="op-winloss">
            <div className="label-tech mb-3">Win / Loss</div>
            <div className="flex items-center gap-5">
              <div>
                <div className="num text-[26px] font-semibold" style={{ color: "#1E7B36" }}>{p.outcomes.won}</div>
                <div className="text-[11px] font-mono uppercase" style={{ color: "var(--text-tertiary)" }}>{L("Gagnées", "Won")}</div>
              </div>
              <div>
                <div className="num text-[26px] font-semibold" style={{ color: "var(--accent)" }}>{p.outcomes.lost}</div>
                <div className="text-[11px] font-mono uppercase" style={{ color: "var(--text-tertiary)" }}>{L("Perdues", "Lost")}</div>
              </div>
              <div>
                <div className="num text-[26px] font-semibold">{p.outcomes.in_progress}</div>
                <div className="text-[11px] font-mono uppercase" style={{ color: "var(--text-tertiary)" }}>{L("En cours", "Open")}</div>
              </div>
            </div>
            <div className="mt-3 text-[12px]" style={{ color: "var(--text-secondary)" }}>
              {p.outcomes.win_rate !== null
                ? <>{L("Taux de conversion :", "Win rate:")} <span className="font-mono font-semibold">{p.outcomes.win_rate}%</span></>
                : L("Pas encore d'issue enregistrée.", "No closed outcomes yet.")}
            </div>
          </Tile>

          <Tile testId="op-account-plan">
            <div className="label-tech mb-3">{L("Plan de compte", "Account plan")}</div>
            {p.account_plan ? (
              <>
                <div className="flex items-center gap-2">
                  <ClipboardText size={16} weight="fill" style={{ color: "var(--accent)" }} />
                  <span className="text-[14px] font-medium">{p.account_plan.title || p.account_plan.name}</span>
                </div>
                {p.account_plan.priority && (
                  <span className="pill mt-2 inline-block uppercase">{L("Priorité", "Priority")}: {p.account_plan.priority}</span>
                )}
                <Link to="/account-plans" className="mt-3 flex items-center gap-1 text-[12px] font-mono hover:text-[var(--accent)] transition-colors"
                      style={{ color: "var(--text-secondary)" }} data-testid="op-plan-link">
                  {L("Ouvrir les plans de compte", "Open account plans")} <ArrowRight size={11} />
                </Link>
              </>
            ) : (
              <div className="text-[13px] py-3" style={{ color: "var(--text-tertiary)" }}>
                {L("Aucun plan de compte pour cet opérateur.", "No account plan for this operator.")}
              </div>
            )}
          </Tile>
        </div>

        {/* AI analysis */}
        {(analysis || analyzing) && (
          <Tile testId="op-ai-analysis">
            <div className="flex items-center gap-2 mb-4">
              <Sparkle size={15} weight="fill" style={{ color: "var(--accent)" }} />
              <div className="label-tech">{L("Analyse IA du compte", "AI account brief")}</div>
            </div>
            {analyzing ? (
              <div className="text-[12px] font-mono py-4" style={{ color: "var(--text-tertiary)" }}>
                {L("Claude analyse le compte…", "Claude is analyzing the account…")}
              </div>
            ) : (
              <div className="text-[13.5px] leading-relaxed whitespace-pre-wrap" style={{ color: "var(--text-primary)" }}
                   data-testid="op-ai-analysis-text">
                {analysis.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
                  part.startsWith("**") && part.endsWith("**")
                    ? <strong key={i}>{part.slice(2, -2)}</strong>
                    : <span key={i}>{part}</span>
                )}
              </div>
            )}
          </Tile>
        )}

        {/* Contacts + Opportunities */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Contacts */}
          <Tile testId="op-contacts" className="min-w-0">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Users size={15} weight="fill" style={{ color: "var(--accent)" }} />
                <div className="label-tech">{L("Contacts", "Contacts")} ({k.contacts})</div>
              </div>
              <Link to={`/contacts?operator=${encodeURIComponent(p.operator)}`}
                    className="text-[11.5px] font-mono hover:text-[var(--accent)] transition-colors"
                    style={{ color: "var(--text-tertiary)" }} data-testid="op-contacts-link">
                {L("Voir tous", "View all")} →
              </Link>
            </div>
            {p.contacts.length === 0 ? (
              <div className="text-[13px] py-6 text-center" style={{ color: "var(--text-tertiary)" }}>
                {L("Aucun contact dans la base pour cet opérateur.", "No contacts for this operator.")}
              </div>
            ) : (
              <div className="space-y-2">
                {p.contacts.slice(0, 8).map((c) => (
                  <button key={c.id} onClick={() => navigate(`/contacts/${c.id}`)}
                          className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-[10px] hover:bg-[var(--bg-subtle)] transition-colors text-left"
                          style={{ border: "1px solid var(--border)" }}
                          data-testid={`op-contact-${c.id}`}>
                    <div className="min-w-0">
                      <div className="text-[13.5px] font-medium truncate">
                        {c.first_name} {c.last_name}
                        {c.decision_maker && <span className="ml-1.5 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-full" style={{ background: "rgba(13,113,221,0.10)", color: "#0D71DD" }}>DM</span>}
                      </div>
                      <div className="text-[11.5px] truncate" style={{ color: "var(--text-tertiary)" }}>{c.title || "—"}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {c.email && <EnvelopeSimple size={13} style={{ color: "var(--text-tertiary)" }} />}
                      {(c.mobile || c.phone) && <Phone size={13} style={{ color: "var(--text-tertiary)" }} />}
                      {c.relationship_score && (
                        <span className="num text-[12px] font-semibold px-1.5 py-0.5 rounded-[6px]"
                              style={{ background: "var(--bg-subtle)", color: c.relationship_score >= 4 ? "#1E7B36" : c.relationship_score >= 3 ? "#B36B00" : "var(--accent)" }}>
                          {c.relationship_score}
                        </span>
                      )}
                      <ArrowRight size={12} style={{ color: "var(--text-tertiary)" }} />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Tile>

          {/* Opportunities */}
          <Tile testId="op-opportunities" className="min-w-0">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Target size={15} weight="fill" style={{ color: "var(--accent)" }} />
                <div className="label-tech">{L("Opportunités", "Opportunities")} ({k.opportunities})</div>
              </div>
              <Link to={`/opportunities?operator=${encodeURIComponent(p.operator)}`}
                    className="text-[11.5px] font-mono hover:text-[var(--accent)] transition-colors"
                    style={{ color: "var(--text-tertiary)" }} data-testid="op-opps-link">
                {L("Voir toutes", "View all")} →
              </Link>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {Object.entries(p.opportunities.by_status).map(([s, n]) => (
                <span key={s} className="flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 rounded-full"
                      style={{ background: "var(--bg-subtle)" }}>
                  <StatusPill status={s} /> {n}
                </span>
              ))}
            </div>
            <div className="overflow-x-auto">
            <table className="tech w-full">
              <thead>
                <tr>
                  <th>{L("Sous-système", "Sub-system")}</th>
                  <th className="text-right">{L("Qté", "Qty")}</th>
                  <th className="text-right">OVH</th>
                  {showPrices && <th className="text-right">{L("Actif", "Active")}</th>}
                </tr>
              </thead>
              <tbody>
                {p.opportunities.top.slice(0, 6).map((o) => (
                  <tr key={o.id}>
                    <td className="text-[12.5px]">{o.sub_system} <span style={{ color: "var(--text-tertiary)" }}>· {o.city}</span></td>
                    <td className="num">{fmtInt(o.total_qty)}</td>
                    <td className="num font-mono text-xs">{o.next_ovh || "—"}</td>
                    {showPrices && <td className="num text-wabtec-red">{o.active_eur ? fmtMoney(o.active_eur) : "—"}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </Tile>
        </div>

        {/* Upcoming OVH + Fleets */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Tile testId="op-upcoming-ovh" className="min-w-0">
            <div className="flex items-center gap-2 mb-4">
              <Trophy size={15} weight="fill" style={{ color: "var(--accent)" }} />
              <div className="label-tech">{L("Prochaines échéances OVH", "Upcoming overhauls")}</div>
            </div>
            {p.opportunities.upcoming_ovh.length === 0 ? (
              <div className="text-[13px] py-4" style={{ color: "var(--text-tertiary)" }}>
                {L("Aucune échéance planifiée.", "No scheduled overhauls.")}
              </div>
            ) : (
              <div className="space-y-2">
                {p.opportunities.upcoming_ovh.slice(0, 7).map((u) => (
                  <div key={u.year} className="flex items-center gap-3" data-testid={`op-ovh-${u.year}`}>
                    <span className="num text-[14px] font-semibold w-12">{u.year}</span>
                    <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: "var(--bg-subtle)" }}>
                      <div className="h-full rounded-full" style={{
                        width: `${Math.min(100, (u.qty / Math.max(...p.opportunities.upcoming_ovh.map((x) => x.qty), 1)) * 100)}%`,
                        background: "var(--accent)", opacity: 0.85,
                      }} />
                    </div>
                    <span className="text-[11.5px] font-mono w-32 text-right" style={{ color: "var(--text-secondary)" }}>
                      {u.count} opps · {fmtInt(u.qty)} {L("u.", "u.")}
                      {showPrices && u.value_eur !== undefined && <> · {fmtMoney(u.value_eur)}</>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Tile>

          <Tile testId="op-fleets" className="min-w-0">
            <div className="flex items-center gap-2 mb-4">
              <Train size={15} weight="fill" style={{ color: "var(--accent)" }} />
              <div className="label-tech">{L("Flottes", "Fleets")}</div>
            </div>
            <div className="overflow-x-auto">
            <table className="tech w-full">
              <thead>
                <tr>
                  <th>{L("Plateforme", "Platform")}</th>
                  <th>{L("Ville / Ligne", "City / Line")}</th>
                  <th className="text-right">{L("Rames", "Trainsets")}</th>
                  <th className="text-right">{L("Année", "Year")}</th>
                </tr>
              </thead>
              <tbody>
                {p.fleets.slice(0, 8).map((f, i) => (
                  <tr key={i}>
                    <td className="text-[12.5px] font-medium">{f.platform || "—"}</td>
                    <td className="text-[12px]" style={{ color: "var(--text-secondary)" }}>{f.city}{f.line ? ` · ${f.line}` : ""}</td>
                    <td className="num">{fmtInt(f.trainsets)}</td>
                    <td className="num font-mono text-xs">{f.start_year || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </Tile>
        </div>

        {/* Full history timeline */}
        <Tile testId="op-timeline">
          <div className="flex items-center gap-2 mb-5">
            <ClockCounterClockwise size={15} weight="fill" style={{ color: "var(--accent)" }} />
            <div className="label-tech">{L("Historique complet", "Full history")} ({p.timeline.length})</div>
          </div>
          {p.timeline.length === 0 ? (
            <div className="text-[13px] py-6 text-center" style={{ color: "var(--text-tertiary)" }}>
              {L("Aucune activité enregistrée — planifiez une visite !", "No activity yet — schedule a visit!")}
            </div>
          ) : (
            <div className="space-y-0">
              {p.timeline.map((e, i) => {
                const meta = TIMELINE_META[e.kind] || TIMELINE_META.comment;
                const Icon = meta.Icon;
                return (
                  <div key={i} className="flex gap-3 relative pb-4" data-testid={`op-timeline-${i}`}>
                    {i < p.timeline.length - 1 && (
                      <div className="absolute left-[13px] top-7 bottom-0 w-px" style={{ background: "var(--border)" }} />
                    )}
                    <div className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center z-[1]"
                         style={{ background: "var(--bg-elevated)", border: `1.5px solid ${meta.color}` }}>
                      <Icon size={13} weight="fill" style={{ color: meta.color }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-[13px] font-medium truncate">{e.title || e.kind}</span>
                        <span className="text-[11px] font-mono shrink-0" style={{ color: "var(--text-tertiary)" }}>
                          {(e.date || "").slice(0, 10)}
                        </span>
                      </div>
                      {e.text && (
                        <p className="text-[12.5px] mt-0.5 line-clamp-2" style={{ color: "var(--text-secondary)" }}>{e.text}</p>
                      )}
                      {e.who && (
                        <span className="text-[10.5px] font-mono" style={{ color: "var(--text-tertiary)" }}>{e.who}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Tile>
      </div>
    </div>
  );
}
